import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { connect } from 'react-redux';

import MillieApi      from 'app/apis/millie';
import CountryPicker  from 'app/components/common/country-picker';
import Icon           from 'app/components/common/icon';
import StandardInput  from 'app/components/common/standard-input';
import countries      from 'app/helpers/countries';
import AppSlx         from 'app/selectors/app';
import AuthSlx        from 'app/selectors/auth';

const defaultGeocodeState = {
  geocodePending: false,
  geocodeSuccess: false,
  geocodeError: false,
  geocodeResult: null,
};

class DonorLocationInput extends React.PureComponent {

  constructor(props) {
    super(props);

    this.state = {
      selectedCountryCode: null,
      inputPostalCode: null,
      isEditing: false,
      ...defaultGeocodeState,
    };

    this.refPcInput = React.createRef();

    this.onChangePostalCode = this.onChangePostalCode.bind(this);
    this.onKeyPressPostalCode = this.onKeyPressPostalCode.bind(this);
    this.onBlurPostalCode = this.onBlurPostalCode.bind(this);
    this.onClickSave = this.onClickSave.bind(this);
    this.onChangeCountry = this.onChangeCountry.bind(this);
    this.onClickEdit = this.onClickEdit.bind(this);
    this.geocode = this.geocode.bind(this);
    this.geocodeDebounced = _.debounce(this.geocode.bind(this), 700);
  }

  componentDidMount() {
    // emit the current user's donorLocation on mount
    const val = this.props.location;
    const userVal = this.props.currentUser?.donorLocation;
    if (!val && !!userVal) {
      this.props.onChange(userVal);
    }
  }

  get isEditing() {
    if (!this.props.location) return true;
    return this.state.isEditing;
  }

  get isExplicitlyEditing() {
    return this.state.isEditing;
  }

  get selectedCountry() {
    const code = [
      this.state.selectedCountryCode,
      this.props.location?.countryCode,
      this.props.ipCountryIso2,
    ].find(c => c);
    return countries.byCode[code] || countries.byCode['US'];
  }

  get inputPostalCode() {
    return (this.state.inputPostalCode != null) ? this.state.inputPostalCode : '';
  }

  get postalCodeInputVal() {
    if (this.state.inputPostalCode != null) return this.state.inputPostalCode;
    return '';
  }

  get saveDisabled() {
    if (!this.state.geocodeSuccess) return true;
    if (!this.selectedLocation) return true;
    return false;
  }

  get selectedLocation() {
    if (!this.state.geocodeSuccess) return null;
    if (this.state.geocodeResult) return this.state.geocodeResult;
    const country = this.selectedCountry;
    if (country.code === 'US') return null; // location object required when country is US
    if (!this.inputPostalCode) return null; // postalCode + country required at a min
    return {postalCode: this.inputPostalCode, countryCode: country.code, country: country.code, countryName: country.name};
  }

  onClickEdit() {
    const {location} = this.props;
    this.setState({
      ...defaultGeocodeState,
      isEditing: true,
      inputPostalCode: (location?.postalCode || null),
      selectedCountryCode: this.selectedCountry.code,
    }, () => {
      this.geocode();
    });
  }

  onChangePostalCode(event) {
    const inputPostalCode = event.target.value;
    this.setState({...defaultGeocodeState, inputPostalCode}, () => {
      this.geocodeDebounced();
    });
  }

  onKeyPressPostalCode(event) {
    const isEnter = event.key === 'Enter';
    if (isEnter) this.onClickSave();
  }

  onBlurPostalCode(event) {
    if (!this.isExplicitlyEditing) {
      this.onClickSave();
    }
  }

  onChangeCountry(selectedCountryCode) {
    this.setState({...defaultGeocodeState, selectedCountryCode}, () => {
      this.geocode();
    });
  }

  onClickSave() {
    if (this.saveDisabled) return;
    const location = this.selectedLocation;
    if (!location) return;
    this.props.onChange(location);
    this.setState({...defaultGeocodeState, isEditing: false});
  }

  getGeocodeKey() {
    const cc = this.selectedCountry.code;
    const pc = this.inputPostalCode.trim();
    if (!cc || !pc) return null;
    return `${cc}-${pc}`;
  }

  getLocationLabel(location) {
    if (!location) return null;
    const {countryCode, city, stateCode} = location;
    const country = countries.byCode[countryCode];
    const isUs = country?.code === 'US';
    if (isUs) return `${city}, ${stateCode}`;
    return [city, country?.flag].filter(p => p).join(' ');
  }

  async geocode() {
    this.props.onChange(null);
    const geocodeKey = this.getGeocodeKey();
    if (!geocodeKey) return;
    this.setState({...defaultGeocodeState, geocodePending: true});
    const newState = {geocodePending: false};
    try {
      const {locations=[]} = await MillieApi.geocodePostalCode(this.inputPostalCode, this.selectedCountry.code);
      newState.geocodeResult = locations[0] || null;
      newState.geocodeSuccess = true;
    } catch (error) {
      newState.geocodeError = true;
    }
    // ignore result if the input has changed since this call began
    const keyChanged = geocodeKey !== this.getGeocodeKey();
    if (keyChanged) return;

    this.setState(newState, () => {
      // go ahead and save if not explicitly editing
      if (!this.isExplicitlyEditing) this.onClickSave();
    });
  }

  renderGeocodeStatusIcon() {
    const {geocodeResult: result, geocodePending: pending, geocodeSuccess: success, geocodeError: error} = this.state;
    const noResults = !!(success && !result);
    if (pending) return <Icon.Loading className="donor-loc-inputs-status-icon" />;
    if (result) return <Icon.CheckCircle1 className="donor-loc-inputs-status-icon green" />;
    if (error || noResults) return null;
    if (result) return this.getLocationLabel(result);
    return success ? 'postal code not found' : '...';
  }

  renderStatic() {
    if (this.isEditing) return null;
    const {location} = this.props;
    if (!location) return null;
    return (
      <div className="donor-loc-static _flex _justify-start _gap-12" onClick={this.onClickEdit}>
        <div><strong>{location.postalCode}</strong> {this.getLocationLabel(location)}</div>
        <Icon.Pencil className="pink-hover donor-loc-static-edit" />
      </div>
    );
  }

  renderForm() {
    if (!this.isEditing) return null;
    const {geocodeResult: result, geocodePending: pending, geocodeSuccess: success, geocodeError: error} = this.state;
    const isUs = this.selectedCountry?.code === 'US';
    const [icon, subtext] = (() => {
      if (pending) {
        return [<Icon.Loading className="donor-loc-inputs-status-icon" />, null];
      }
      if (result) {
        const icn = this.isExplicitlyEditing
          ? null
          : <Icon.CheckCircle1 className="donor-loc-inputs-status-icon green" />;
        return [icn, null]
      }
      if (!success && !error) return [null, null];
      const icn = <Icon.AlertCircle className={`donor-loc-inputs-status-icon ${isUs ? 'danger' : ''}`} />;
      const msg = success
        ? 'Postal code not found'
        : 'Oops! Something went wrong';
      const spn = <span className={`donor-loc-form-subtext ${isUs ? 'danger' : ''}`}>{msg}</span>
      return [icn, spn];
    })();

    return (<>
      <div className="donor-loc-form _flex _justify-start _gap-6">
        <div className="donor-loc-inputs _flex _gap-0 _justify-start _flex-1">
          <CountryPicker className="donor-loc-inputs-country" code={this.selectedCountry?.code} onChange={this.onChangeCountry} justFlag />
          <StandardInput className="donor-loc-inputs-pc _flex-1" ref={this.refPcInput} value={this.inputPostalCode} onChange={this.onChangePostalCode} name="postalCode" label="Postal Code" onKeyPress={this.onKeyPressPostalCode} onBlur={this.onBlurPostalCode} />
          {icon}
        </div>
        {this.isExplicitlyEditing && (
          <button className="btn blue secondary" onClick={this.onClickSave} disabled={this.saveDisabled}>Update</button>
        )}
      </div>
      {subtext}
    </>);
  }

  render() {
    return (
      <div className="donor-loc">
        {this.renderStatic()}
        {this.renderForm()}
      </div>
    );
  }

}

DonorLocationInput.propTypes = {
  location: PropTypes.shape({
    postalCode: PropTypes.string,
    city: PropTypes.string,
    stateCode: PropTypes.string,
    countryCode: PropTypes.string,
  }),
  onChange: PropTypes.func.isRequired,
};

const stateToProps = (state) => ({
  currentUser: AuthSlx.currentUser(state),
  ipCountryIso2: AppSlx.ipCountryIso2(state),
});
const dispatchToProps = (dispatch) => ({});

export default connect(stateToProps, dispatchToProps)(DonorLocationInput);
